import { X } from 'lucide-react';
import { SENIORITY_LEVELS, SeniorityBreakdown, SeniorityLevel } from '@/utils/rolesGrouping';

const LABELS: Record<SeniorityLevel, string> = {
	lead: 'Lead',
	senior: 'Sr',
	mid: 'Mid',
	junior: 'Jr',
	unknown: 'Other',
};

const COLORS: Record<SeniorityLevel, string> = {
	lead: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
	senior: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
	mid: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
	junior: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
	unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const INACTIVE = 'bg-gray-50 text-gray-400 dark:bg-gray-900 dark:text-gray-600';

interface SeniorityPillsProps {
	breakdown: SeniorityBreakdown;
	size?: 'xs' | 'sm';
	activeLevels?: Set<SeniorityLevel>;
	onToggle?: (level: SeniorityLevel) => void;
	onClear?: () => void;
}

export const SeniorityPills = ({ breakdown, size = 'xs', activeLevels, onToggle, onClear }: SeniorityPillsProps) => {
	const entries = SENIORITY_LEVELS
		.map(level => ({ level, count: breakdown[level] }))
		.filter(e => e.count > 0);

	if (entries.length === 0) return null;

	const padding = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs';
	const interactive = !!onToggle;
	const allActive = !activeLevels || activeLevels.size === SENIORITY_LEVELS.length;
	const showClear = interactive && !allActive && !!onClear;

	return (
		<div className="flex items-center gap-1 flex-wrap">
			{entries.map(({ level, count }) => {
				const active = !activeLevels || activeLevels.has(level);
				const colorClass = active ? COLORS[level] : INACTIVE;
				const base = `inline-flex items-center gap-1 rounded-sm font-medium tabular-nums ${padding} ${colorClass}`;

				if (interactive) {
					return (
						<button
							key={level}
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onToggle!(level);
							}}
							className={`${base} cursor-pointer transition-opacity hover:opacity-80`}
							aria-pressed={active}
						>
							<span>{LABELS[level]}</span>
							<span>{count}</span>
						</button>
					);
				}

				return (
					<span key={level} className={base}>
						<span>{LABELS[level]}</span>
						<span>{count}</span>
					</span>
				);
			})}
			{showClear && (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onClear!();
					}}
					className="inline-flex items-center justify-center rounded-sm p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
					title="Clear filter"
					aria-label="Clear seniority filter"
				>
					<X className="h-3 w-3" />
				</button>
			)}
		</div>
	);
};
